import { useState } from "react";
import { Link } from "react-router-dom";

// Caprocure — Canadian homepage (approved preview composition).
// Layout, wording, sections and confidentiality message are preserved from the
// approved HTML preview. Registration and login actions are wired to existing
// platform routes (/register, /login). No claims about supplier coverage across
// every Canadian province.
export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="caprocure-landing" data-testid="landing-page">
      {/* Scoped styles — mirror the approved preview exactly */}
      <style>{`
        .caprocure-landing{
          font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          color:#0b2d5b; background:#fff;
          overflow-x:hidden;
        }
        .caprocure-landing *{ box-sizing:border-box; }
        .caprocure-landing a{ color:inherit; text-decoration:none; }
        .cl-wrap{ max-width:1200px; margin:auto; padding:0 28px; }

        /* Header */
        .cl-header{ height:100px; display:flex; align-items:center; border-bottom:1px solid #e7ecf2; background:#fff; position:sticky; top:0; z-index:40; }
        .cl-header .cl-wrap{ display:flex; align-items:center; justify-content:space-between; width:100%; gap:28px; }
        .cl-brand{ display:inline-flex; align-items:center; flex-shrink:0; }
        .cl-brand img{ display:block; height:44px; width:auto; }
        .cl-nav-group{ display:flex; align-items:center; gap:28px; flex-shrink:0; min-width:0; }
        .cl-nav{ display:flex; align-items:center; gap:32px; font-size:.91rem; font-weight:650; white-space:nowrap; }
        .cl-nav a{ white-space:nowrap; }
        .cl-nav a:hover, .cl-text-link:hover{ color:#d62828; }
        .cl-nav-actions{ display:flex; align-items:center; gap:14px; flex-shrink:0; }
        .cl-login{ font-size:.9rem; font-weight:700; white-space:nowrap; }
        .caprocure-landing a.cl-button,
        .caprocure-landing button.cl-button,
        .cl-button{ display:inline-flex; align-items:center; justify-content:center; gap:9px; border-radius:6px; background:#d62828; color:#fff; padding:15px 23px; font-weight:750; border:1px solid #d62828; transition:transform .2s, background .2s; cursor:pointer; text-decoration:none; white-space:nowrap; }
        .caprocure-landing a.cl-button:hover,
        .cl-button:hover{ background:#b51c20; color:#fff; transform:translateY(-2px); }
        .caprocure-landing a.cl-button.outline,
        .cl-button.outline{ background:#fff; color:#0b2d5b; border-color:#c5d1de; }
        .caprocure-landing a.cl-button.outline:hover,
        .cl-button.outline:hover{ background:#f1f5f9; color:#0b2d5b; }
        .cl-menu{ display:none; background:none; border:1px solid #bdcbd9; color:#0b2d5b; border-radius:6px; padding:9px 12px; font-weight:700; cursor:pointer; }

        /* Hero */
        .cl-hero{ background:linear-gradient(118deg,#f8fafc 0%,#f8fafc 49%,#e9f1f8 100%); overflow:hidden; }
        .cl-hero .cl-wrap{ min-height:560px; display:grid; grid-template-columns:1fr .94fr; align-items:center; gap:55px; padding-top:56px; padding-bottom:65px; }
        .cl-eyebrow{ text-transform:uppercase; letter-spacing:.23em; color:#b91e2b; font-size:.78rem; font-weight:800; }
        .cl-hero h1{ font-size:clamp(2.4rem,5vw,5rem); letter-spacing:-.055em; line-height:1.03; max-width:680px; margin:21px 0; color:#0b2d5b; overflow-wrap:break-word; }
        .cl-hero p{ font-size:1.14rem; line-height:1.7; color:#425a73; max-width:550px; margin:0 0 30px; }
        .cl-hero-actions{ display:flex; gap:12px; flex-wrap:wrap; }
        .cl-hero-art{ position:relative; min-height:390px; border-radius:14px; overflow:hidden; background:linear-gradient(150deg,#0b2d5b,#184875 60%,#3a749b); box-shadow:0 28px 70px #0b2d5b26; }
        .cl-hero-art:before{ content:""; position:absolute; inset:0; background:linear-gradient(130deg,transparent 30%,#ffffff0c 30% 34%,transparent 34% 56%,#ffffff0b 56% 59%,transparent 59%),radial-gradient(circle at 80% 20%,#ffffff22,transparent 40%); }
        .cl-hero-art .cl-leaf{ position:absolute; right:8%; top:10%; filter:drop-shadow(0 12px 8px #061d3044); transform:rotate(8deg); }
        .cl-art-card{ position:absolute; background:#fff; border:1px solid #e6ebf0; border-radius:9px; box-shadow:0 15px 30px #001a3350; padding:21px; left:9%; right:18%; bottom:12%; color:#0b2d5b; }
        .cl-art-card small{ letter-spacing:.17em; text-transform:uppercase; color:#72849a; font-weight:750; }
        .cl-art-card strong{ display:block; font-size:1.28rem; margin:9px 0; }
        .cl-art-card .cl-line{ height:8px; border-radius:6px; background:#e9eff5; margin:11px 0; width:80%; }
        .cl-art-card .cl-line.short{ width:50%; }
        .cl-art-card .cl-badge{ display:inline-block; background:#eef7f4; color:#18745c; font-size:.77rem; padding:7px 10px; border-radius:4px; font-weight:750; margin-top:4px; }

        /* Sections */
        .cl-section{ padding:90px 0; }
        .cl-section-title{ font-size:clamp(2rem,4vw,3rem); letter-spacing:-.045em; line-height:1.13; margin:13px 0 18px; color:#0b2d5b; overflow-wrap:break-word; }
        .cl-section-lead{ color:#52677e; line-height:1.65; font-size:1.04rem; max-width:640px; }
        .cl-steps{ display:grid; grid-template-columns:repeat(3,1fr); gap:20px; margin-top:42px; }
        .cl-step{ border:1px solid #e4eaf1; border-radius:10px; padding:30px; background:#fff; }
        .cl-number{ font-size:.92rem; font-weight:850; color:#d62828; }
        .cl-step h3{ font-size:1.26rem; margin:18px 0 11px; color:#0b2d5b; }
        .cl-step p{ line-height:1.6; color:#52677e; margin:0; }

        .cl-split{ background:#f5f8fb; }
        .cl-split-grid{ display:grid; grid-template-columns:1fr 1fr; gap:24px; }
        .cl-panel{ background:#fff; border:1px solid #e2e9f0; border-radius:10px; padding:42px; }
        .cl-panel h2{ font-size:2rem; letter-spacing:-.04em; margin:14px 0; color:#0b2d5b; overflow-wrap:break-word; }
        .cl-panel p{ color:#52677e; line-height:1.7; margin:0 0 24px; }
        .cl-text-link{ font-weight:800; border-bottom:2px solid #d62828; padding-bottom:5px; color:#0b2d5b; }

        .cl-privacy{ background:#0b2d5b; color:#fff; }
        .cl-privacy-grid{ display:grid; grid-template-columns:1fr 1fr; gap:80px; align-items:center; }
        .cl-privacy .cl-section-title{ color:#fff; }
        .cl-privacy p{ color:#d0dfed; line-height:1.7; font-size:1.07rem; }
        .cl-privacy-list{ display:grid; gap:14px; }
        .cl-privacy-item{ border:1px solid #44627d; border-radius:8px; padding:20px 22px; color:#e3edf6; }
        .cl-privacy-item strong{ display:block; color:#fff; margin-bottom:5px; }

        .cl-cta{ text-align:center; }
        .cl-cta p{ margin:0 auto 28px; color:#52677e; max-width:600px; line-height:1.65; }

        .cl-footer{ background:#071e3b; color:#dae5ef; padding:42px 0; }
        .cl-footer .cl-wrap{ display:flex; justify-content:space-between; align-items:center; gap:20px; flex-wrap:wrap; }
        .cl-footer small{ color:#9fb4c8; }
        .cl-footer-brand img{ display:block; height:34px; width:auto; filter:brightness(0) invert(1); }

        /* Responsive — collapse to mobile menu whenever the full nav no longer fits comfortably */
        @media (max-width:1024px){
          .cl-header{ height:82px; }
          .cl-menu{ display:block; }
          .cl-nav-group{ display:none; }
          .cl-nav-group.open{
            display:flex; position:absolute; left:0; right:0; top:82px; z-index:20;
            background:#fff; padding:20px 28px; box-shadow:0 18px 30px #0b2d5b18;
            flex-direction:column; align-items:stretch; gap:16px;
            border-top:1px solid #e7ecf2;
          }
          .cl-nav-group.open .cl-nav{
            flex-direction:column; align-items:flex-start; gap:14px; font-size:1rem;
          }
          .cl-nav-group.open .cl-nav-actions{
            flex-direction:column; align-items:stretch; gap:12px;
            padding-top:14px; border-top:1px solid #e7ecf2;
          }
          .cl-nav-group.open .cl-login{ padding:6px 0; }
          .cl-nav-group.open .cl-button{ width:100%; }
          .cl-hero .cl-wrap{ grid-template-columns:1fr; gap:32px; min-height:0; }
          .cl-hero-art{ min-height:350px; }
          .cl-privacy-grid{ gap:35px; grid-template-columns:1fr; }
          .cl-steps{ grid-template-columns:1fr; }
          .cl-split-grid{ grid-template-columns:1fr; }
        }
        @media (max-width:540px){
          .cl-wrap{ padding:0 20px; }
          .cl-hero .cl-wrap{ padding-top:56px; padding-bottom:65px; }
          .cl-hero h1{ font-size:2.2rem; }
          .cl-hero p{ font-size:1rem; }
          .cl-hero-art{ min-height:260px; }
          .cl-art-card{ padding:15px; left:6%; right:8%; bottom:8%; }
          .cl-art-card strong{ font-size:1rem; }
          .cl-hero-art .cl-leaf{ width:96px; height:auto; right:6%; top:8%; }
          .cl-section{ padding:60px 0; }
          .cl-panel{ padding:26px; }
          .cl-hero-actions .cl-button{ width:100%; }
          .cl-brand img{ height:34px; }
          .cl-footer .cl-wrap{ flex-direction:column; align-items:flex-start; }
        }
      `}</style>

      {/* ============================ HEADER ============================ */}
      <header className="cl-header" id="top">
        <div className="cl-wrap">
          <Link to="/" className="cl-brand" aria-label="Caprocure home" data-testid="brand-logo-link">
            <img src="/caprocure-logo.png" alt="Caprocure" />
          </Link>

          <button
            className="cl-menu"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            {menuOpen ? "Close" : "Menu"}
          </button>

          <div className={`cl-nav-group ${menuOpen ? "open" : ""}`} data-testid="header-nav-group">
            <nav className="cl-nav" data-testid="primary-nav">
              <a href="#how-it-works" onClick={() => setMenuOpen(false)}>For buyers</a>
              <a href="#suppliers" onClick={() => setMenuOpen(false)}>For suppliers</a>
              <a href="#how-it-works" onClick={() => setMenuOpen(false)}>How it works</a>
              <a href="#privacy" onClick={() => setMenuOpen(false)}>Privacy</a>
            </nav>
            <div className="cl-nav-actions">
              <Link to="/login" className="cl-login" data-testid="nav-signin-btn" onClick={() => setMenuOpen(false)}>Log in</Link>
              <Link to="/register" className="cl-button" data-testid="nav-register-btn" onClick={() => setMenuOpen(false)}>
                Get started <span aria-hidden="true">↗</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* ============================ HERO ============================ */}
      <section className="cl-hero">
        <div className="cl-wrap">
          <div>
            <div className="cl-eyebrow">Procurement made simple</div>
            <h1 data-testid="hero-heading">Connecting Canadian buyers with trusted suppliers.</h1>
            <p>
              Tell us what your business needs. Caprocure helps you source the right products through
              a clear, managed quotation process.
            </p>
            <div className="cl-hero-actions">
              <Link to="/register?role=buyer" className="cl-button" data-testid="hero-submit-request-btn">
                Submit a request <span aria-hidden="true">↗</span>
              </Link>
              <a href="#how-it-works" className="cl-button outline" data-testid="hero-how-it-works-btn">
                See how it works
              </a>
            </div>
          </div>

          <div className="cl-hero-art" aria-label="Illustration of a procurement request">
            <svg
              className="cl-leaf"
              aria-hidden="true"
              viewBox="0 0 100 110"
              width="140"
              height="150"
            >
              <path
                fill="#d62828"
                d="M50 3 58 25 69 18 67 35 82 33 77 46 95 48 79 63 83 75 59 72 53 86 53 108 47 108 47 86 41 72 17 75 21 63 5 48 23 46 18 33 33 35 31 18 42 25Z"
              />
            </svg>
            <div className="cl-art-card">
              <small>Procurement request</small>
              <strong>Find the right supplier</strong>
              <div className="cl-line" />
              <div className="cl-line short" />
              <span className="cl-badge">A simpler way to source</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ HOW IT WORKS ============================ */}
      <section id="how-it-works" className="cl-section">
        <div className="cl-wrap">
          <div className="cl-eyebrow">The process</div>
          <h2 className="cl-section-title">From request to delivery, with clarity at each step.</h2>
          <p className="cl-section-lead">
            Our platform gives buyers and suppliers one place to follow a transaction from the initial
            request through fulfillment.
          </p>
          <div className="cl-steps">
            <div className="cl-step">
              <span className="cl-number">01 / REQUEST</span>
              <h3>Tell us what you need</h3>
              <p>Buyers submit the product, quantity, specifications, and delivery details.</p>
            </div>
            <div className="cl-step">
              <span className="cl-number">02 / QUOTE</span>
              <h3>Review your offer</h3>
              <p>Caprocure coordinates supplier quotations and provides a clear buyer quote.</p>
            </div>
            <div className="cl-step">
              <span className="cl-number">03 / TRACK</span>
              <h3>Follow the order</h3>
              <p>See delivery and payment milestones as the transaction progresses.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ SPLIT: BUYERS / SUPPLIERS ============================ */}
      <section className="cl-section cl-split">
        <div className="cl-wrap cl-split-grid">
          <article className="cl-panel" id="buyers">
            <div className="cl-eyebrow">For Canadian buyers</div>
            <h2>Source with confidence.</h2>
            <p>
              Send one clear request, review the offer prepared for you, and follow your order through
              delivery.
            </p>
            <Link to="/register?role=buyer" className="cl-text-link" data-testid="split-buyer-link">
              Get started as a buyer ↗
            </Link>
          </article>
          <article className="cl-panel" id="suppliers">
            <div className="cl-eyebrow">For suppliers</div>
            <h2>Find relevant opportunities.</h2>
            <p>
              Receive selected requests that match what you supply and submit your quotation through
              the platform.
            </p>
            <Link to="/register?role=supplier" className="cl-text-link" data-testid="split-supplier-link">
              Join as a supplier ↗
            </Link>
          </article>
        </div>
      </section>

      {/* ============================ PRIVACY ============================ */}
      <section id="privacy" className="cl-section cl-privacy">
        <div className="cl-wrap cl-privacy-grid">
          <div>
            <div className="cl-eyebrow" style={{ color: "#ff777d" }}>Built on trust</div>
            <h2 className="cl-section-title">
              Your pricing stays confidential. Supplier quotes stay private.
            </h2>
            <p>We manage quotations so each party sees the information relevant to its role.</p>
          </div>
          <div className="cl-privacy-list">
            <div className="cl-privacy-item">
              <strong>For buyers</strong>
              You see the price quoted to your business.
            </div>
            <div className="cl-privacy-item">
              <strong>For suppliers</strong>
              Your quote is handled privately and is not shown to competing suppliers.
            </div>
            <div className="cl-privacy-item">
              <strong>For Caprocure</strong>
              Internal supplier costs and margins stay inside our administration tools.
            </div>
          </div>
        </div>
      </section>

      {/* ============================ CTA ============================ */}
      <section className="cl-section cl-cta">
        <div className="cl-wrap">
          <div className="cl-eyebrow">Start here</div>
          <h2 className="cl-section-title">Ready to simplify sourcing?</h2>
          <p>
            Open the Caprocure platform to request products or explore supplier opportunities.
          </p>
          <Link to="/register" className="cl-button" data-testid="cta-visit-platform-btn">
            Visit the platform <span aria-hidden="true">↗</span>
          </Link>
        </div>
      </section>

      {/* ============================ FOOTER ============================ */}
      <footer className="cl-footer">
        <div className="cl-wrap">
          <div className="cl-footer-brand">
            <img src="/caprocure-logo.png" alt="Caprocure" />
          </div>
          <small>© {new Date().getFullYear()} JMB Investment Group Ltd., operating as Caprocure™ · Ontario, Canada</small>
        </div>
      </footer>
    </div>
  );
}
